import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import * as apicall from "../../components/Apicall";
import "./BidRequests.css";

// UtilStop "Bid Your Price" requests (apiengine src/routes/bids). A customer named a
// price below the listed price and paid a token; every eligible pro is invited. Here the
// pro accepts the customer's price, counters with a higher one, or declines — until the
// bid closes. The customer then books one pro; the job lands in that pro's normal
// bookings. Customer name/address/contact stay hidden until they book.

const POLL_MS = 30000;

// UAE Dirham sign — same glyph the website uses (PriceText.jsx).
const Dirham = () => (
  <svg viewBox="0 0 344.84 299.91" className="br-dirham" fill="currentColor" aria-label="AED" role="img">
    <path d="M342.14,140.96l2.7,2.54v-7.72c0-17-11.92-30.84-26.56-30.84h-23.41C278.49,36.7,222.69,0,139.68,0c-52.86,0-59.65,0-109.71,0,0,0,15.03,12.63,15.03,52.4v52.58h-27.68c-5.38,0-10.43-2.08-14.61-6.01l-2.7-2.54v7.72c0,17.01,11.92,30.84,26.56,30.84h18.44s0,29.99,0,29.99h-27.68c-5.38,0-10.43-2.07-14.61-6.01l-2.7-2.54v7.71c0,17,11.92,30.82,26.56,30.82h18.44s0,54.89,0,54.89c0,38.65-15.03,50.06-15.03,50.06h109.71c85.62,0,139.64-36.96,155.38-104.98h32.46c5.38,0,10.43,2.07,14.61,6l2.7,2.54v-7.71c0-17-11.92-30.83-26.56-30.83h-18.9c.32-4.88.49-9.87.49-15s-.18-10.11-.51-14.99h28.17c5.37,0,10.43,2.07,14.61,6.01ZM89.96,15.01h45.86c61.7,0,97.44,27.33,108.1,89.94l-153.96.02V15.01ZM136.21,284.93h-46.26v-89.98l153.87-.02c-9.97,56.66-42.07,88.38-107.61,90ZM247.34,149.96c0,5.13-.11,10.13-.34,14.99l-157.04.02v-29.99l157.05-.02c.22,4.84.33,9.83.33,15Z" />
  </svg>
);

const Price = ({ amount, strike }) => (
  <span className={`br-price${strike ? " br-strike" : ""}`}>
    <Dirham />
    {(Number(amount) || 0).toFixed(2)}
  </span>
);

function formatTime(t) {
  if (!t || t === "null") return "";
  const [h, m] = String(t).split(":");
  const hour = Number(h) % 24;
  return `${hour % 12 || 12}:${m} ${hour < 12 ? "AM" : "PM"}`;
}

function timeLeft(expiresAt, offset, now) {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - (now + offset);
  if (ms <= 0) return "closing…";
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h}h ${String(m).padStart(2, "0")}m left` : `${m}m left`;
}

const STATUS = {
  pending: { label: "New", cls: "br-chip-new" },
  accepted: { label: "You accepted", cls: "br-chip-ok" },
  countered: { label: "You countered", cls: "br-chip-warn" },
  declined: { label: "Declined", cls: "br-chip-muted" },
  lost: { label: "Closed", cls: "br-chip-muted" },
  won: { label: "Booked you", cls: "br-chip-won" },
};

export default function BidRequests() {
  const [items, setItems] = useState([]);
  const [linked, setLinked] = useState(true);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("open");
  const [busyId, setBusyId] = useState(null);
  const [counter, setCounter] = useState(null); // { item, price }
  const [offset, setOffset] = useState(0);
  const [now, setNow] = useState(Date.now());

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await apicall.get("vendor_bid_requests");
      setLinked(res?.linked !== false);
      setItems(Array.isArray(res?.data) ? res.data : []);
      if (res?.server_time) setOffset(new Date(res.server_time).getTime() - Date.now());
    } catch (e) {
      if (!silent) toast.error(e?.message || "Could not load bid requests");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const poll = setInterval(() => load(true), POLL_MS);
    const tick = setInterval(() => setNow(Date.now()), 30000);
    return () => {
      clearInterval(poll);
      clearInterval(tick);
    };
  }, [load]);

  const isOpen = (i) => i.bid_status === "open" && !["declined", "lost", "won"].includes(i.response_status);
  const openItems = useMemo(() => items.filter(isOpen), [items]);
  const closedItems = useMemo(() => items.filter((i) => !isOpen(i)), [items]);
  const list = tab === "open" ? openItems : closedItems;

  async function respond(item, action, price) {
    setBusyId(item.response_id);
    try {
      const res = await apicall.post({ action, price }, `vendor_bid_requests/${item.response_id}/respond`);
      if (res?.success) {
        toast.success(
          action === "accept" ? "Accepted — the customer can now book you" : action === "counter" ? "Counter sent" : "Declined"
        );
        setCounter(null);
        await load(true);
      } else {
        toast.error(res?.message || "Could not update this bid");
      }
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="br-page">
      <div className="br-head">
        <div>
          <h2 className="br-title">Bid Requests</h2>
          <p className="br-sub">
            Customers near you named their price. Accept it, counter, or decline before the bid closes — the customer books
            one pro.
          </p>
        </div>
        <div className="br-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={tab === "open"} className={tab === "open" ? "active" : ""} onClick={() => setTab("open")}>
            Open {openItems.length ? `(${openItems.length})` : ""}
          </button>
          <button type="button" role="tab" aria-selected={tab === "closed"} className={tab === "closed" ? "active" : ""} onClick={() => setTab("closed")}>
            Closed
          </button>
        </div>
      </div>

      {!linked && (
        <div className="br-note">
          Your account isn't linked to a UtilStop pro profile yet. Bid requests appear here when this account's phone number
          matches your UtilStop pro registration.
        </div>
      )}

      {loading ? (
        <div className="br-empty">Loading…</div>
      ) : list.length === 0 ? (
        <div className="br-empty">{tab === "open" ? "No open bid requests right now." : "No closed bids yet."}</div>
      ) : (
        <div className="br-list">
          {list.map((item) => {
            const chip = STATUS[item.response_status] || STATUS.pending;
            const open = isOpen(item);
            const busy = busyId === item.response_id;
            const yourMove = open && (item.response_status === "pending" || item.last_action_by === "user");
            return (
              <div key={item.response_id} className={`br-card${open ? "" : " br-card-closed"}`}>
                <div className="br-card-main">
                  <div className="br-card-top">
                    <span className="br-service">{item.service_name || "Service"}</span>
                    <span className={`br-chip ${chip.cls}`}>{chip.label}</span>
                    {open && item.expires_at && <span className="br-timer">⏱ {timeLeft(item.expires_at, offset, now)}</span>}
                  </div>
                  {item.items?.length > 0 && <p className="br-items">{item.items.join(" · ")}</p>}
                  <p className="br-meta">
                    {[item.service_date, formatTime(item.service_time), item.area].filter(Boolean).join(" · ")}
                  </p>
                  {item.response_status === "pending" && item.vendor_price != null && (
                    <p className="br-hint">The customer countered your offer of <Price amount={item.vendor_price} />.</p>
                  )}
                  {item.response_status === "won" && (
                    <p className="br-hint br-hint-won">
                      Booked at <Price amount={item.final_price} /> — the job is in your bookings.
                    </p>
                  )}
                  {item.response_status === "lost" && item.bid_status === "booked" && (
                    <p className="br-hint">The customer booked another pro.</p>
                  )}
                  {item.bid_status === "expired" && item.response_status !== "declined" && (
                    <p className="br-hint">The bid closed without a booking.</p>
                  )}
                </div>

                <div className="br-card-side">
                  <div className="br-prices">
                    <span className="br-label">Customer&apos;s price</span>
                    <span className="br-big"><Price amount={item.user_price} /></span>
                    <span className="br-label">
                      Listed <Price amount={item.listed_price} strike />
                    </span>
                    {item.response_status === "countered" && (
                      <span className="br-label br-yours">
                        Your counter <Price amount={item.vendor_price} />
                      </span>
                    )}
                  </div>

                  {open && (
                    <div className="br-actions">
                      {yourMove || item.response_status === "countered" ? (
                        <>
                          <button type="button" className="br-btn br-btn-primary" disabled={busy} onClick={() => respond(item, "accept")}>
                            Accept <Price amount={item.user_price} />
                          </button>
                          <button
                            type="button"
                            className="br-btn br-btn-outline"
                            disabled={busy}
                            onClick={() =>
                              setCounter({
                                item,
                                price: Math.round(Math.max(item.vendor_price || 0, item.user_price * 1.1, item.user_price + 1)),
                              })
                            }
                          >
                            Counter
                          </button>
                        </>
                      ) : (
                        <span className="br-waiting">Waiting for the customer</span>
                      )}
                      <button type="button" className="br-btn br-btn-ghost" disabled={busy} onClick={() => respond(item, "decline")}>
                        Decline
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {counter && (
        <div className="br-modal-backdrop" onClick={() => setCounter(null)}>
          <div className="br-modal" onClick={(e) => e.stopPropagation()}>
            <div className="br-modal-head">
              <h3>Send a counter offer</h3>
              <button type="button" className="br-close" onClick={() => setCounter(null)} aria-label="Close">
                ×
              </button>
            </div>
            <div className="br-compare">
              <div>
                <span className="br-label">Customer&apos;s price</span>
                <strong><Price amount={counter.item.user_price} /></strong>
              </div>
              <div>
                <span className="br-label">Listed price</span>
                <strong><Price amount={counter.item.listed_price} /></strong>
              </div>
            </div>
            <label className="br-label" htmlFor="br-counter">Your price</label>
            <div className="br-input">
              <Dirham />
              <input
                id="br-counter"
                type="number"
                min={counter.item.user_price}
                step="0.5"
                value={counter.price}
                onChange={(e) => setCounter({ ...counter, price: e.target.value })}
              />
            </div>
            {Number(counter.price) <= Number(counter.item.user_price) && (
              <p className="br-error">A counter must be higher than the customer&apos;s price — accept it instead.</p>
            )}
            <div className="br-modal-actions">
              <button
                type="button"
                className="br-btn br-btn-outline"
                disabled={busyId === counter.item.response_id}
                onClick={() => respond(counter.item, "accept")}
              >
                Accept <Price amount={counter.item.user_price} /> instead
              </button>
              <button
                type="button"
                className="br-btn br-btn-primary"
                disabled={busyId === counter.item.response_id || !(Number(counter.price) > Number(counter.item.user_price))}
                onClick={() => respond(counter.item, "counter", Number(counter.price))}
              >
                Send counter · <Price amount={counter.price} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
